// Helper único para llamar al backend nuevo de Atlas (atlas-backend).
//
// Todos los endpoints migrados desde n8n usan la MISMA auth: el JWT de la
// sesión de Supabase en `Authorization: Bearer <access_token>`. El backend
// deriva el user_id del token — por eso `user_id` NO viaja en el body de
// estos endpoints (a diferencia de los webhooks viejos de n8n, que usaban el
// token estático GENERATE_AUTH_TOKEN + user_id explícito).
//
// Devuelve el `Response` CRUDO a propósito: los flujos migrados necesitan
// distinguir status (200 resuelto / 202 pending / 400 validation / 402 sin
// créditos) — la interpretación vive en el helper de cada flujo.

import { supabase } from './supabase';
import { ATLAS_BACKEND_URL } from '../config';

export async function atlasBackendFetch(
  path: string,
  body: Record<string, unknown>,
  /** Extras del fetch (ej. `signal` para timeout del caller). */
  init?: Pick<RequestInit, 'signal'>,
): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('No hay sesión activa para llamar al backend de Atlas.');
  }
  return fetch(`${ATLAS_BACKEND_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    ...(init?.signal ? { signal: init.signal } : {}),
  });
}

/** GET autenticado al backend de Atlas (mismo JWT que atlasBackendFetch). */
export async function atlasBackendGet(path: string): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('No hay sesión activa para llamar al backend de Atlas.');
  }
  return fetch(`${ATLAS_BACKEND_URL}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Request autenticado con método libre (GET / POST / DELETE). `atlasBackendFetch` es POST
 * fijo y `atlasBackendGet` no lleva body; Equipo necesita DELETE para apagar el link y
 * sacar gente (spec 2026-09-11-invitaciones-equipo §2.1).
 */
export async function atlasBackendRequest(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: Record<string, unknown>,
): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('No hay sesión activa para llamar al backend de Atlas.');
  }
  return fetch(`${ATLAS_BACKEND_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

export interface SettledJob {
  status: 'succeeded' | 'failed';
  output: Record<string, unknown> | null;
  /** Taxonomía del backend (policy_violation, provider_timeout, technical…). */
  errorType: string | null;
  /** Mensaje YA mostrable (castellano + aclaración de créditos) que arma el backend. */
  userMessage: string | null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Cierra el ciclo del 202 de los flujos wait-for-result (creator/audio/clone):
 * el backend venció su wait pero el job sigue vivo — se pollea `GET /v1/jobs/:id`
 * hasta que termine. Antes de esto el front quedaba CIEGO tras el 202: si el job
 * fallaba después, el usuario no se enteraba nunca (gap del mapa de errores 25/7).
 * Devuelve null si se agota el tope (el job puede seguir corriendo igual).
 */
export async function pollJobUntilSettled(
  jobId: string,
  opts?: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal },
): Promise<SettledJob | null> {
  const deadline = Date.now() + (opts?.timeoutMs ?? 10 * 60 * 1000);
  const intervalMs = opts?.intervalMs ?? 5000;
  while (Date.now() < deadline) {
    if (opts?.signal?.aborted) return null;
    await sleep(intervalMs);
    let json: any = null;
    try {
      const res = await atlasBackendGet(`/v1/jobs/${jobId}`);
      if (!res.ok) continue; // blip puntual (o 404 raro): reintentar
      json = await res.json();
    } catch {
      continue; // red caída momentánea → siguiente ronda
    }
    const job = json?.job;
    if (job?.status === 'succeeded') {
      return { status: 'succeeded', output: job.output ?? null, errorType: null, userMessage: null };
    }
    if (job?.status === 'failed') {
      return {
        status: 'failed',
        output: null,
        errorType: job.error?.type ?? 'technical',
        userMessage: job.user_message ?? null,
      };
    }
    // queued/running → seguir esperando
  }
  return null;
}
